/**
 * Смуга відкритих сторінок («вкладки», як панель вікон 1С) — під головним
 * меню, над блоком сторінки. Клік — перехід, × — закрити, середня кнопка
 * миші — закрити, ПКМ — меню «закрити інші / всі».
 *
 * Це НЕ окремі вікна: SPA рендерить одну сторінку за раз, тому перемикання
 * = звичайна навігація (дані списків беруться з кешу react-query миттєво).
 * Незбережені правки документів і карток довідників живуть у чернетці
 * (useFormDraft, ч.584/ч.1059) — сторінка запропонує їх відновити.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { permissionForPath } from '@/auth/routePermissions';
import { usePermissions } from '@/auth/usePermissions';
import { useConfirm } from '@/components/ConfirmProvider';
import { clearDraft, hasDraft, purgeExpiredDrafts } from '@/store/formDrafts';
import { useOpenTabs } from '@/store/openTabs';
import { isDocumentRoute, isTabbableRoute, resolveRouteTitle } from '@/store/tabTitle';

/**
 * ч.950 (рішення власника): у вкладки йдуть і ДОКУМЕНТИ/картки, і сторінки
 * МЕНЮ/СУБМЕНЮ (списки) — раніше лише документи. Друковані форми і службові
 * сторінки — ніколи.
 */
function isTrackable(pathname: string): boolean {
  if (pathname === '/login' || pathname === '/select-tenant') return false;
  if (/\/print(-view)?(\/|$)/.test(pathname)) return false;
  return isTabbableRoute(pathname);
}

/** «/goods-sales/new» → базовий шлях «/goods-sales» (для заміни вкладки). */
function docBase(pathname: string): string {
  return pathname.replace(/\/(new|[0-9a-f-]{8,}|\d+)$/i, '');
}

export default function OpenTabsBar() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const nav = useNavigate();
  const loc = useLocation();
  const { tabs, open, autoRename, remember, close, closeOthers, closeAll, replacePath } = useOpenTabs();
  const [menu, setMenu] = useState<{ x: number; y: number; path: string } | null>(null);

  // Протерміновані чернетки (>10 год) прибираємо на старті застосунку.
  useEffect(() => { purgeExpiredDrafts(); }, []);

  const path = loc.pathname;
  const trackable = isTrackable(path);
  // ч.1068: вкладку заводимо лише для ДОЗВОЛЕНОГО маршруту і коли права вже
  // відомі. Інакше AutoRouteGuard за мить перенаправить на головну, а вкладка
  // забороненої сторінки лишилась би «фантомом» (з назвою головної). Часто
  // трапляється після перемикання кабінету: розділ, доступний в одному
  // кабінеті, в іншому (інша роль) — заборонений.
  const { has, isLoading: permsLoading } = usePermissions();
  const requiredPerm = permissionForPath(path);
  const allowed = !permsLoading && (requiredPerm === null || has(requiredPerm));

  // ч.950 (баг власника): після збереження НОВОГО документа редактор робить
  // navigate('/X/{id}', { replace: true }) — вкладка «/X/новий» має СТАТИ
  // вкладкою збереженого документа, а не лишитись поруч другою.
  const prevPath = useRef<string | null>(null);
  useEffect(() => {
    const from = prevPath.current;
    prevPath.current = path;
    if (!trackable || !from || from === path) return;
    const wasNew = from.endsWith('/new');
    const nowSaved = isDocumentRoute(path) && !path.endsWith('/new');
    if (wasNew && nowSaved && docBase(from) === docBase(path)) {
      replacePath(from, path);
    }
  }, [path, trackable, replacePath]);

  // Реєструємо перехід як вкладку.
  useEffect(() => {
    if (!trackable || !allowed) return;
    open(path, resolveRouteTitle(path, t));
  }, [path, trackable, allowed, open, t]);

  // ч.1378 (SUP-1329FE54): вкладка памʼятає повну адресу (параметри звіту, відбори) — клік
  // по ній повертає туди, де її залишили, а не на сторінку з параметрами за замовчуванням.
  const href = `${loc.pathname}${loc.search}`;
  useEffect(() => {
    if (!trackable || !allowed) return;
    remember(path, href);
  }, [path, href, trackable, allowed, remember]);

  // Назва вкладки з заголовка сторінки (<h2> у .app__main) — щоб вкладка
  // казала «Видаткова накладна № GS-2026-0005», а не просто «Продажі», і
  // без правки сотні редакторів. Явний useTabTitle має пріоритет.
  useEffect(() => {
    if (!trackable || !allowed) return;
    const host = document.querySelector('.app__main');
    if (!host) return;
    const pick = () => {
      const raw = host.querySelector('h2')?.textContent ?? '';
      const title = raw
        .replace(/\s+/g, ' ')
        .replace(/[✓✔].*$/u, '')          // «✓ проведено» — статус, не назва
        .replace(/\(\s*[\d\s\u00a0]+\)\s*$/u, '')  // «(1 234)» — лічильник
        .trim();
      if (title && title.length <= 80) autoRename(path, title);
    };
    pick();
    const mo = new MutationObserver(pick);
    mo.observe(host, { childList: true, subtree: true, characterData: true });
    // Документ вантажиться асинхронно; довше 8 с чекати немає сенсу.
    const stop = window.setTimeout(() => mo.disconnect(), 8000);
    return () => { mo.disconnect(); window.clearTimeout(stop); };
  }, [path, trackable, allowed, autoRename]);

  useEffect(() => {
    if (!menu) return;
    const off = () => setMenu(null);
    document.addEventListener('click', off);
    return () => document.removeEventListener('click', off);
  }, [menu]);

  if (tabs.length === 0) return null;

  // Закриття вкладки: якщо у документі є НЕЗБЕРЕЖЕНІ правки — питаємо
  // (у базу нічого не пишемо, чернетка просто стирається). Далі —
  // перехід на сусідню вкладку, якщо закрили активну.
  const closeTab = async (p: string) => {
    if (hasDraft(p)) {
      const ok = await confirm({
        title: 'Незбережені зміни',
        // ч.1059: чернетки мають і картки довідників — текст для обох.
        message: 'На цій сторінці є незбережені зміни. Закрити вкладку без '
          + 'збереження? Щоб зберегти — натисніть «скасувати» і збережіть.',
        okLabel: 'закрити без збереження',
        cancelLabel: 'скасувати',
        danger: true,
      });
      if (!ok) return;
      clearDraft(p);
    }
    const idx = tabs.findIndex((x) => x.path === p);
    close(p);
    if (p !== path) return;
    const nextTab = tabs[idx + 1] ?? tabs[idx - 1];
    nav(nextTab ? (nextTab.href ?? nextTab.path) : '/');
  };

  return (
    <div className="tabbar">
      {tabs.map((tab) => {
        const active = tab.path === path;
        return (
          <div
            key={tab.path}
            className={`tabbar__tab${active ? ' tabbar__tab--active' : ''}`}
            title={tab.title}
            onClick={() => { if (!active) nav(tab.href ?? tab.path); }}
            // Середня кнопка: на mousedown браузер вмикає автоскрол
            // (курсор-«приціл») і жест губиться — глушимо, закриваємо одразу.
            onMouseDown={(e) => {
              if (e.button === 1) { e.preventDefault(); void closeTab(tab.path); }
            }}
            onAuxClick={(e) => { if (e.button === 1) e.preventDefault(); }}
            onContextMenu={(e) => {
              e.preventDefault();
              setMenu({ x: e.clientX, y: e.clientY, path: tab.path });
            }}
          >
            <span className="tabbar__label">{tab.title}</span>
            <button
              type="button"
              className="tabbar__close"
              title="Закрити"
              onClick={(e) => { e.stopPropagation(); void closeTab(tab.path); }}
            >×</button>
          </div>
        );
      })}

      {menu && (
        <div className="tabbar__menu" style={{ left: menu.x, top: menu.y }}
             onClick={(e) => e.stopPropagation()}>
          <button type="button" onClick={() => { void closeTab(menu.path); setMenu(null); }}>
            закрити
          </button>
          <button type="button" onClick={() => {
            const keep = tabs.find((x) => x.path === menu.path);
            closeOthers(menu.path); nav(keep?.href ?? menu.path); setMenu(null);
          }}>
            закрити інші
          </button>
          <button type="button" onClick={() => { closeAll(); setMenu(null); }}>
            закрити всі
          </button>
        </div>
      )}
    </div>
  );
}
